import { FC } from 'react'

import {
  Box,
  Button,
  Flex,
  IconArrowUpRight,
  Link,
  PreviewCard,
  Primary13Regular,
  Text
} from '@sequencehq/core-components'

import { QuotePreviewCard } from 'Automations/components'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { useQuoteData } from '../../../../hooks/useQuoteData'
import { SyncErrorQuoteSnapshot, SyncErrorTaskInput } from '../../types'
import { ViewInIntegrationAction } from '../../ViewInIntegrationAction'

const TEST_ID = 'watchtower.notification.integrationSyncError.recordCard'

export const QuoteRecordCard: FC<{
  taskId: string
  snapshot: SyncErrorQuoteSnapshot
}> = ({ taskId, snapshot }) => {
  const { quote, customer } = useQuoteData(snapshot.quoteId ?? undefined)
  const hasQuoteData = quote && customer

  if (hasQuoteData) {
    return (
      <Box data-testid={TEST_ID}>
        <QuotePreviewCard
          quote={quote}
          customer={customer}
          action={<QuoteActions taskId={taskId} quoteId={snapshot.quoteId} />}
        />
      </Box>
    )
  }

  const subtitles = [
    snapshot.quoteNumber,
    snapshot.customerName,
    snapshot.status
  ]
    .filter((value): value is string => Boolean(value))
    .map(value => (
      <Text key={value} {...Primary13Regular}>
        {value}
      </Text>
    ))

  if (!snapshot.name && subtitles.length === 0) {
    return null
  }

  return (
    <PreviewCard
      data-testid={TEST_ID}
      showBorder
      title={snapshot.name ?? 'Quote'}
      subtitle={subtitles}
      content={<QuoteActions taskId={taskId} quoteId={snapshot.quoteId} />}
    />
  )
}

const QuoteActions: FC<{ taskId: string; quoteId?: string | null }> = ({
  taskId,
  quoteId
}) => (
  <Flex gap="8px" marginTop="8px">
    <ViewInIntegrationAction
      taskId={taskId}
      data-testid={`${TEST_ID}.viewInIntegration`}
    />
    {quoteId && (
      <Button
        as={Link.Unstyled}
        to={`/quotes/${quoteId}`}
        variant="secondary"
        leadingIcon={<IconArrowUpRight />}
        data-testid={`${TEST_ID}.viewQuote`}
      >
        View quote
      </Button>
    )}
  </Flex>
)
